import {
  BITHOMP_API_BASE,
  BITHOMP_API_KEY,
  BITHOMP_API_KEY_HEADER,
  BITHOMP_TRANSACTIONS_PATH,
  BITHOMP_TRANSACTIONS_QUERY,
  BITHOMP_WHALE_PATH,
  BITHOMP_WHALE_RECEIVERS_PATH,
  BITHOMP_WHALE_SENDERS_PATH,
  BITHOMP_WHALE_SUBMITTERS_PATH,
} from './config'

export type BithompTransaction = {
  id: string
  type: string
  address?: string
  sequence?: number
  specification?: any
  outcome?: {
    timestamp?: string
    fee?: string
    result?: string
    deliveredAmount?: {
      currency?: string
      value?: string
    }
  }
}

type StoredTransactions = Record<string, BithompTransaction[]>

const LOCAL_TX_STORAGE_KEY = 'xrpl:recentTxs'
const LOCAL_TX_LIMIT = 20

const loadLocalTransactions = (): StoredTransactions => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(LOCAL_TX_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as StoredTransactions
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

const saveLocalTransactions = (data: StoredTransactions) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LOCAL_TX_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    // ignore storage errors
  }
}

export const getLocalTransactions = (account: string): BithompTransaction[] => {
  if (!account) return []
  const data = loadLocalTransactions()
  const txs = data[account] || []
  return Array.isArray(txs) ? txs : []
}

export const recordRecentTransaction = (account: string, txHash: string, type?: string) => {
  if (!account || !txHash) return
  const data = loadLocalTransactions()
  const existing = data[account] || []
  const next = [
    {
      id: txHash,
      type: type || 'Transaction',
      outcome: {
        result: 'tesSUCCESS',
        timestamp: new Date().toISOString(),
      },
    },
    ...existing.filter((tx) => tx.id !== txHash),
  ].slice(0, LOCAL_TX_LIMIT)
  data[account] = next
  saveLocalTransactions(data)
}

const mergeTransactions = (remote: BithompTransaction[], local: BithompTransaction[]) => {
  const ordered = new Map<string, BithompTransaction>()
  remote.forEach((tx) => {
    ordered.set(tx.id, tx)
  })
  local.forEach((tx) => {
    if (!ordered.has(tx.id)) {
      ordered.set(tx.id, tx)
    }
  })
  return Array.from(ordered.values())
}

const buildHeaders = () => {
  const headers: Record<string, string> = {}
  if (BITHOMP_API_KEY) {
    headers[BITHOMP_API_KEY_HEADER] = BITHOMP_API_KEY
  }
  return headers
}

const toUrl = (path: string, query?: string) => {
  const base = BITHOMP_API_BASE.endsWith('/') ? BITHOMP_API_BASE : `${BITHOMP_API_BASE}/`
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  if (!query) {
    return `${base}${cleanPath}`
  }
  return `${base}${cleanPath}?${query}`
}

export const fetchTransactionsByAddress = async (address: string, query = BITHOMP_TRANSACTIONS_QUERY) => {
  const local = getLocalTransactions(address)
  try {
    const url = toUrl(`${BITHOMP_TRANSACTIONS_PATH}/${address}`, query)
    const response = await fetch(url, { headers: buildHeaders() })
    if (!response.ok) {
      if (local.length) {
        return local
      }
      throw new Error('Failed to fetch transactions')
    }
    const data = await response.json()
    const remote = Array.isArray(data) ? (data as BithompTransaction[]) : []
    return mergeTransactions(remote, local)
  } catch (error) {
    if (local.length) {
      return local
    }
    throw error
  }
}

export const fetchWhaleTransactions = async (query = 'limit=20') => {
  const response = await fetch(toUrl(BITHOMP_WHALE_PATH, query), { headers: buildHeaders() })
  if (!response.ok) {
    throw new Error('Failed to fetch whale transactions')
  }
  return response.json()
}

export const fetchWhaleReceivers = async (query = 'limit=20') => {
  const response = await fetch(toUrl(BITHOMP_WHALE_RECEIVERS_PATH, query), { headers: buildHeaders() })
  if (!response.ok) {
    throw new Error('Failed to fetch whale receivers')
  }
  return response.json()
}

export const fetchWhaleSenders = async (query = 'limit=20') => {
  const response = await fetch(toUrl(BITHOMP_WHALE_SENDERS_PATH, query), { headers: buildHeaders() })
  if (!response.ok) {
    throw new Error('Failed to fetch whale senders')
  }
  return response.json()
}

export const fetchWhaleSubmitters = async (query = 'limit=20') => {
  const response = await fetch(toUrl(BITHOMP_WHALE_SUBMITTERS_PATH, query), { headers: buildHeaders() })
  if (!response.ok) {
    throw new Error('Failed to fetch whale submitters')
  }
  return response.json()
}
